import { useState } from "react";
import { NodeViewWrapper, NodeViewContent, NodeViewProps } from "@tiptap/react";
import { Button, IconButton, Input, Modal, Select, Stack, Title } from "@introist/react-foundation/v2";
import { Pencil, X } from "lucide-react";
import {
  buildConditionLabel,
  ConditionalOperator,
  OPERATOR_OPTIONS,
  OPERATORS_REQUIRING_VALUE
} from "../utils/conditionalSerializer";
import { Tag } from "../../../../../modules/system/tags/useTags";
import { TagSelectorList } from "../../../../../modules/system/tags/TagSelector";

type EditStep = "field" | "operator";

// Minimal shape needed for edit state (avoids constructing fake Tag objects)
type FieldRef = { tag: string; displayTitle: string };

export const ConditionalBlockView = ({ node, editor, getPos, updateAttributes }: NodeViewProps) => {
  const [editOpen, setEditOpen] = useState(false);
  const [editStep, setEditStep] = useState<EditStep>("operator");
  const [editOperator, setEditOperator] = useState<ConditionalOperator>("truthy");
  const [editValue, setEditValue] = useState<string>("");
  const [editField, setEditField] = useState<FieldRef | null>(null);

  const { condition, operator, value, conditionTitle } = node.attrs as {
    condition: string;
    operator: ConditionalOperator;
    value: string | null;
    conditionTitle: string | null;
  };

  const displayTitle = conditionTitle ?? condition;
  const label = buildConditionLabel(displayTitle, operator, value);

  const handleDelete = () => {
    const pos = typeof getPos === "function" ? getPos() : null;
    if (pos === null || !editor) return;
    editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + node.nodeSize })
      .run();
  };

  const handleOpenEdit = () => {
    setEditStep("operator");
    setEditOperator(operator);
    setEditValue(value ?? "");
    setEditField({ tag: condition, displayTitle: conditionTitle ?? condition });
    setEditOpen(true);
  };

  const handleTagSelect = (tag: Tag) => {
    setEditField({ tag: tag.tag, displayTitle: tag.fullTitle ?? tag.title });
    setEditStep("operator");
  };

  const handleConfirmEdit = () => {
    if (!editField) return;
    updateAttributes({
      condition: editField.tag,
      conditionTitle: editField.displayTitle,
      operator: editOperator,
      value: OPERATORS_REQUIRING_VALUE.includes(editOperator) ? editValue : null
    });
    setEditOpen(false);
  };

  const needsValue = OPERATORS_REQUIRING_VALUE.includes(editOperator);
  const canConfirm = !!editField && (!needsValue || editValue.trim().length > 0);

  return (
    <NodeViewWrapper>
      <div className="conditional-block">
        <div className="conditional-block__header" contentEditable={false}>
          <Stack gap="xSmall" alignItems="center" style={{ flex: 1 }}>
            <span className="conditional-block__if-label">IF</span>
            <span className="conditional-block__condition-label">{label}</span>
          </Stack>
          <IconButton icon={<Pencil size={12} color="var(--palette-warning-hovered)" />} size="small" onClick={handleOpenEdit} onMouseDown={(e: React.MouseEvent) => e.stopPropagation()} />
          <IconButton icon={<X size={12} color="var(--palette-warning-hovered)" />} size="small" onClick={handleDelete} onMouseDown={(e: React.MouseEvent) => e.stopPropagation()} />
        </div>
        <NodeViewContent className="conditional-block__body" />
      </div>

      <Modal title="Edit condition" open={editOpen} onClose={() => setEditOpen(false)}>
        <Stack vertical gap="large" style={{ minWidth: "360px" }}>
          {editStep === "field" ? (
            <Stack vertical gap="small">
              <Title variant="bold">Select a field to check</Title>
              <div style={{ maxHeight: "340px", overflow: "auto" }}>
                <TagSelectorList onSelect={handleTagSelect} />
              </div>
            </Stack>
          ) : (
            <Stack vertical gap="medium">
              <Stack vertical gap="xSmall">
                <Title variant="bold">Field</Title>
                <Stack gap="xSmall" alignItems="center">
                  <span
                    style={{
                      color: "var(--palette-foreground-subdued)",
                      fontSize: "var(--font-size-small)"
                    }}
                  >
                    {editField?.displayTitle}
                  </span>
                  <Button variant="blended" size="small" onClick={() => setEditStep("field")}>
                    Change
                  </Button>
                </Stack>
              </Stack>

              <Stack vertical gap="xSmall">
                <Title variant="bold">Condition</Title>
                <Select
                  options={OPERATOR_OPTIONS}
                  value={editOperator}
                  onSelect={opt => setEditOperator(opt.key as ConditionalOperator)}
                  placeholder="Select condition..."
                />
              </Stack>

              {needsValue && (
                <Stack vertical gap="xSmall">
                  <Title variant="bold">Value</Title>
                  <Input
                    value={editValue}
                    onChange={val => setEditValue(val)}
                    placeholder="Enter value..."
                    autoFocus
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === "Enter" && canConfirm) handleConfirmEdit();
                    }}
                  />
                </Stack>
              )}

              <Stack justifyContent="flex-end" gap="small">
                <Button variant="outlined" onClick={() => setEditOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleConfirmEdit} disabled={!canConfirm}>
                  Update
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Modal>
    </NodeViewWrapper>
  );
};
